// frontend/src/pages/FranchisingDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function FranchisingDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [agenti, setAgenti] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [ordini, setOrdini] = useState([]);

  const isFranchising = (me?.role || "").toUpperCase() === "FRANCHISING";
  const myUsername = (me?.username || "").trim();
  const myStoreName = (me?.store_name || me?.store || "").trim();

  const headers = () => {
    const token = localStorage.getItem("access");
    return { Authorization: `Bearer ${token}` };
  };

  const euro = (n) =>
    `€ ${Number(n || 0).toFixed(2).replace(".", ",")}`;

  const fetchAll = async () => {
    setLoading(true);
    setErr("");
    try {
      // profilo
      const meRes = await fetch(`${API_URL}/api/me/`, { headers: headers() });
      const meData = await meRes.json();
      if (!meRes.ok) throw new Error(meData?.detail || "Errore caricamento profilo");
      setMe(meData);

      // agenti
      try {
        const aRes = await fetch(`${API_URL}/api/agenti/`, { headers: headers() });
        const aData = await aRes.json();
        setAgenti(Array.isArray(aData) ? aData : (aData?.results || []));
      } catch {
        setAgenti([]);
      }

      // aziende
      try {
        const zRes = await fetch(`${API_URL}/api/aziende/`, { headers: headers() });
        const zData = await zRes.json();
        setAziende(Array.isArray(zData) ? zData : (zData?.results || []));
      } catch {
        setAziende([]);
      }

      // ordini (endpoint già filtra: franchising + suoi agenti/aziende)
      const oRes = await fetch(`${API_URL}/api/ordini/ricevuti/`, { headers: headers() });
      const oData = await oRes.json();
      if (!oRes.ok) throw new Error(oData?.detail || "Errore caricamento ordini");
      setOrdini(Array.isArray(oData) ? oData : (oData?.results || []));
    } catch (e) {
      setErr(e.message || "Errore");
      setAgenti([]);
      setAziende([]);
      setOrdini([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // --- filtri locali per "i miei" (stesso store) ---
  const isSameStore = (obj) => {
    const s = (obj?.store_name || obj?.store || "").trim();
    return myStoreName && s && s === myStoreName;
  };

  const agentiMiei = useMemo(
    () => (agenti || []).filter(isSameStore),
    [agenti, myStoreName]
  );

  const aziendeMie = useMemo(
    () => (aziende || []).filter(isSameStore),
    [aziende, myStoreName]
  );

  // "Movimenti del franchising stesso": ordini creati dal franchising (non degli agenti)
  const ordiniMiei = useMemo(() => {
    const getCreator = (o) =>
      (o?.creato_da || o?.sender_username || "").trim();
    return (ordini || []).filter((o) => getCreator(o) === myUsername);
  }, [ordini, myUsername]);

  // KPI ordini (solo "miei")
  const kpi = useMemo(() => {
    const nonCanceled = ordiniMiei.filter((o) => (o.status || "").toUpperCase() !== "CANCELED");
    const paid = nonCanceled.filter((o) => !!o.is_paid);
    const unpaid = nonCanceled.filter((o) => !o.is_paid);

    const sum = (arr) => arr.reduce((acc, o) => acc + Number(o.total || 0), 0);

    return {
      countAll: nonCanceled.length,
      countPaid: paid.length,
      countUnpaid: unpaid.length,
      sumPaid: sum(paid),
      sumUnpaid: sum(unpaid),
    };
  }, [ordiniMiei]);

  // piccola guardia ruolo
  if (!loading && !isFranchising) {
    return (
      <div className="container mt-4">
        <TornaAlMenu to="/" />
        <div className="alert alert-warning">
          Questa pagina è riservata agli utenti <strong>FRANCHISING</strong>.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <TornaAlMenu to="/" />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="m-0">🏬 Dashboard Franchising</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary" onClick={fetchAll}>🔄 Aggiorna</button>
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      {loading ? (
        <p className="text-muted">Caricamento…</p>
      ) : (
        <>
          {/* KPI compatti: solo dati essenziali */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-3">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="text-muted small">Ordini (del franchising)</div>
                  <div className="display-6">{kpi.countAll}</div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-3">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="text-muted small">Pagati</div>
                  <div className="display-6">{kpi.countPaid}</div>
                  <div className="text-muted small mt-1">{euro(kpi.sumPaid)}</div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-3">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="text-muted small">Non pagati</div>
                  <div className="display-6">{kpi.countUnpaid}</div>
                  <div className="text-muted small mt-1">{euro(kpi.sumUnpaid)}</div>
                </div>
              </div>
            </div>

            <div className="col-6 col-md-1">
              <div className="card shadow-sm">
                <div className="card-body text-center">
                  <div className="text-muted small">Agenti</div>
                  <div className="h3 m-0">{agentiMiei.length}</div>
                </div>
              </div>
            </div>

            <div className="col-6 col-md-2">
              <div className="card shadow-sm">
                <div className="card-body text-center">
                  <div className="text-muted small">Aziende</div>
                  <div className="h3 m-0">{aziendeMie.length}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Ultimi ordini del franchising (non degli agenti) */}
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <strong>Ultimi ordini del franchising</strong>
                <button className="btn btn-sm btn-outline-primary" onClick={() => nav("/ordini")}>
                  Apri Ordini
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              {ordiniMiei.length === 0 ? (
                <div className="p-3 text-muted">Nessun ordine.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0 align-middle">
                    <thead>
                      <tr>
                        <th style={{width: 90}}>ID</th>
                        <th>Data</th>
                        <th>Per</th>
                        <th>Stato</th>
                        <th className="text-end">Totale</th>
                        <th className="text-end">Pagamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordiniMiei
                        .slice() // copia
                        .sort((a, b) => (a.date > b.date ? -1 : 1))
                        .slice(0, 8)
                        .map((o) => (
                          <tr key={o.id}>
                            <td>#{o.id}</td>
                            <td>{o.date}</td>
                            <td>{o.per_nome || o.cliente || "-"}</td>
                            <td>
                              <span className="badge bg-secondary">
                                {(o.status_it || o.status || "").toString()}
                              </span>
                            </td>
                            <td className="text-end">{euro(o.total)}</td>
                            <td className="text-end">
                              {o.is_paid ? (
                                <span className="badge bg-success">Pagato</span>
                              ) : (
                                <span className="badge bg-warning text-dark">Non pagato</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
